import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import {
  columnProperties,
  getHeadingMatchedPropertyKey,
  importDataContainsHeader,
  mapMatchedColumnsToRowData,
} from './helpers';
import Select from '../../../../../../../../components/global/Select';
import Button from '../../../../../../../../components/global/Button';
import ColumnPreview from './ColumnPreview';
import CheckboxIcon from '../../../../../../../../components/icons/CheckboxIcon';
import useVirtualPageView from '../../../../../../services/analytics/useVirtualPageView';
import CustomControl from '../../../../../../../../components/global/CustomControl';
import { ALBUM_SUBTYPE } from '../../../../../../../../constants/albums';
import UniformConfigurationSelector from './components/UniformConfigurationSelector';
import ContactLinkSelector from './components/ContactLinkSelector';

const claimedAndGrantingDefaults = {
  [ALBUM_SUBTYPE.CAMPS]: {
    claimed: true,
    grantPurchase: true,
    lock: true,
    claimedAndGrantingCopy:
      'All members to your event will have face matching turned on and photos will be delivered to them automatically.',
  },
  default: {
    claimedAndGrantingCopy:
      'All members to your event will have face matching turned on and photos will be delivered to them automatically.',
  },
};

const propTypes = {
  data: PropTypes.arrayOf(PropTypes.array),
  loading: PropTypes.bool,
  onContinue: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  contactLabel: PropTypes.string,
  forHierarchyItems: PropTypes.bool,
  albumSubType: PropTypes.oneOf(Object.values(ALBUM_SUBTYPE)),
  supportedColumnProperties: PropTypes.arrayOf(PropTypes.string),
};

const ColumnMatcher = ({
  onContinue,
  onCancel,
  data,
  loading,
  contactLabel = 'contact',
  forHierarchyItems = false,
  isPhotoFinder = false,
  isPro = false,
  albumSubType,
  supportedColumnProperties = columnProperties,
}) => {
  useVirtualPageView({
    title: 'Import Contacts - Map Columns',
    path: '/contacts-import/map',
  });

  const subTypeDefaults =
    claimedAndGrantingDefaults[albumSubType] ||
    claimedAndGrantingDefaults.default;

  const isClaimOnlyPro = isPro && !isPhotoFinder;
  const lockClaimedAndGranting = subTypeDefaults?.lock || isClaimOnlyPro;

  const claimedDefault = (() => {
    return lockClaimedAndGranting
      ? subTypeDefaults?.claimed ?? false
      : subTypeDefaults?.claimed ?? null;
  })();

  const [claimed, setClaimed] = useState(claimedDefault);

  const purchaseDefault = (() => {
    return lockClaimedAndGranting
      ? subTypeDefaults?.grantPurchase ?? false
      : subTypeDefaults?.grantPurchase ?? null;
  })();

  const [grantPurchase, setGrantPurchase] = useState(purchaseDefault);
  const [uniformConfiguration, setUniformConfiguration] = useState(undefined);
  const [contactLinkConfiguration, setContactLinkConfiguration] = useState({
    albumFolderId: null,
    albums: [],
  });
  const hasHeader = importDataContainsHeader(supportedColumnProperties, data);
  const [header, ...rows] = data;
  const [columns, setColumns] = useState(
    hasHeader
      ? header.map((heading, index) => {
          return {
            label: heading,
            rowDataIndex: index,
            matchedPropertyKey: getHeadingMatchedPropertyKey(
              supportedColumnProperties,
              heading,
            ),
          };
        })
      : // If there's no header simply start with no selections and let the user manually map the columns.
        header.map((_, index) => ({
          label: 'N/A (no header detected)',
          rowDataIndex: index,
          matchedPropertyKey: '',
        })),
  );
  const [error, setError] = useState('');

  const allColumnsMapped = columns.every(column => !!column.matchedPropertyKey);

  const onPropertyChange = (selectedColumn, selectedPropertyKey) => {
    // Do nothing, the user selected the same property.
    if (selectedColumn.matchedPropertyKey === selectedPropertyKey) {
      return;
    }

    setColumns(prevColumns =>
      prevColumns.map(column => {
        if (column.rowDataIndex === selectedColumn.rowDataIndex) {
          return {
            ...column,
            matchedPropertyKey: selectedPropertyKey,
          };
        }

        // Reset the property that had occupied before
        if (
          column.matchedPropertyKey === selectedPropertyKey &&
          selectedPropertyKey !== 'skip'
        ) {
          return {
            ...column,
            matchedPropertyKey: '',
          };
        }

        return column;
      }),
    );
  };

  const onSubmit = () => {
    if (!allColumnsMapped) {
      setError('Please select a property or "don’t import" for every column.');
      return;
    }

    onContinue(
      mapMatchedColumnsToRowData(columns, hasHeader ? rows : data),
      claimed,
      grantPurchase,
      uniformConfiguration,
      contactLinkConfiguration,
    );
  };

  return (
    <div className="column-matcher-wrap">
      <header className="column-matcher-header">
        <h4 className="mb-1">
          Map columns in your file to {contactLabel} properties
        </h4>
        <p>
          Each column header below should be mapped to a {contactLabel}{' '}
          property. Some of these have already been mapped based on their names.
          Anything that hasn&rsquo;t been mapped yet can be manually mapped to a{' '}
          {contactLabel} property with the dropdown menu. You can always choose
          &ldquo;Don&rsquo;t import column&rdquo; for unnecessary columns.
        </p>

        {forHierarchyItems && (
          <p className="text-sm my-1">
            <b>Reminder</b>: if you have reference images, please add them to
            the reference image tab, located under the <b>Manage Members</b>{' '}
            dropdown.
          </p>
        )}

        <UniformConfigurationSelector
          contactLabel={contactLabel}
          uniformConfiguration={uniformConfiguration}
          setUniformConfiguration={setUniformConfiguration}
        />
        <ContactLinkSelector
          contactLabel={contactLabel}
          contactLinkConfiguration={contactLinkConfiguration}
          setContactLinkConfiguration={setContactLinkConfiguration}
        />
      </header>

      <table
        className={classNames({
          'table table-striped table-lg table-column-matcher': true,
          'is-loading': loading,
        })}
      >
        <thead>
          <tr>
            <th>Matched</th>
            <th className="text-left">Column Name</th>
            <th className="text-left">Preview</th>
            <th className="text-left text-capitalize">
              {contactLabel} Property
            </th>
          </tr>
        </thead>

        <tbody>
          {columns.map((column, columnIndex) => {
            return (
              <tr key={columnIndex}>
                <td>
                  {column.matchedPropertyKey ? <CheckboxIcon size={16} /> : '-'}
                </td>
                <td className="text-left">{column.label}</td>
                <td className="text-left column-matcher-preview">
                  <ColumnPreview
                    index={columnIndex}
                    rows={hasHeader ? rows : data}
                  />
                </td>
                <td>
                  <Select
                    className="m-0"
                    label="Select a property"
                    labelSrOnly
                    id={column.label}
                    value={column.matchedPropertyKey}
                    onChange={event => {
                      onPropertyChange(column, event.target.value);
                    }}
                  >
                    <option value="">Select a property</option>
                    {supportedColumnProperties.map(property => {
                      const isSelectedElsewhere =
                        columns.some(
                          x => x.matchedPropertyKey === property.key,
                          // Nit: Only show the selected checkbox if it's
                          // not the currently selected option.
                        ) && property.key !== column.matchedPropertyKey;

                      return (
                        <option key={property.key} value={property.key}>
                          {property.label} {isSelectedElsewhere && '✓'}
                        </option>
                      );
                    })}
                    <option value="skip">Don&rsquo;t import</option>
                  </Select>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {forHierarchyItems && !lockClaimedAndGranting && (
        <div className="w-100 d-flex flex-column justify-content-end my-2 pr-2">
          <div className="d-flex align-items-center mb-2">
            <CustomControl
              id="claimed-subbed"
              type="radio"
              name="claimed-subbed"
              checked={grantPurchase && claimed}
              onChange={() => {
                setGrantPurchase(true);
                setClaimed(true);
              }}
              label="Add these members to your event and turn on face matching and
                delivery to parents automatically"
              size="md"
              className="d-inline"
            />
          </div>
          <div className="d-flex align-items-center">
            <CustomControl
              id="unclaimed-unsubbed"
              type="radio"
              name="unclaimed-unsubbed"
              checked={grantPurchase === false && claimed === false}
              onChange={() => {
                setGrantPurchase(false);
                setClaimed(false);
              }}
              label="Add these members to your event and turn on face matching but
                not delivery to parents (parents can turn this on by signing up,
                using the QR code, join code or link found in the marketing
                toolbox via Invite)"
              size="md"
              className="d-inline"
            />
          </div>
          <p className="ml-3 mt-1 mb-0 text-xs text-danger">
            Note: members with email-only as their contact method and no
            delivery option are intended for internal matching purposes only.
            Parents will be unable to turn on delivery for these members.
          </p>
        </div>
      )}
      {forHierarchyItems && isClaimOnlyPro && (
        <div className="w-100 d-flex flex-column justify-content-end my-2 pr-2">
          <div className="d-flex align-items-center mb-2">
            <CustomControl
              id="claimed-only"
              type="checkbox"
              name="claimed-only"
              checked={claimed}
              onChange={() => {
                setClaimed(true);
              }}
              label="Deliver photos to parents automatically (parents may still require a subscription or purchase a Digital Photo Pass to see their matches)."
              size="md"
              className="d-inline"
            />
          </div>
        </div>
      )}
      {forHierarchyItems &&
        lockClaimedAndGranting &&
        subTypeDefaults.claimedAndGrantingCopy &&
        claimed &&
        grantPurchase && (
          <div className="w-100 d-flex flex-column justify-content-end my-2 pr-2">
            {subTypeDefaults.claimedAndGrantingCopy}
          </div>
        )}
      <footer className="column-matcher-footer">
        {error && <p className="text-danger text-sm mb-0">{error}</p>}

        {onCancel && (
          <Button
            onClick={onCancel}
            btnStyle="link"
            className="ml-2"
            disabled={loading}
            btnSize="sm"
          >
            Cancel Import
          </Button>
        )}
        <Button
          onClick={onSubmit}
          className="ml-2"
          disabled={
            loading ||
            (forHierarchyItems && (grantPurchase === null || claimed === null))
          }
          btnSize="sm"
        >
          {loading ? 'Processing...' : 'Continue'}
        </Button>
      </footer>
    </div>
  );
};

ColumnMatcher.propTypes = propTypes;

export default ColumnMatcher;
